import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Card, Grid, IconButton, makeStyles, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import clientPageStyle from "features/client/styles";
import { CheckBox } from "@material-ui/icons";
import { emptyObject } from "constants/defaultValues";
import { get } from "@totalsoft/change-tracking-react";

const useStyles = makeStyles(clientPageStyle);

function Client({ index, partner, selectedClientLens, resetSelectedClientLens, isReadOnly }) {
  const { t } = useTranslation();

  const selected = useMemo(
    () =>
      (selectedClientLens |> get) &&
      (selectedClientLens.partnerId |> get) === partner.partnerId &&
      (selectedClientLens.origin |> get) === partner.origin &&
      (selectedClientLens.bureauId |> get) === partner.bureauId &&
      (selectedClientLens.taxId |> get) === partner.taxId,
    [partner, selectedClientLens]
  );

  const styleProps = useMemo(() => ({ selected }), [selected]);
  const classes = useStyles(styleProps);

  const handleSelect = useCallback(() => {
    resetSelectedClientLens({ ...partner, isSelectFromList: true });
  }, [partner, resetSelectedClientLens]);

  const handleDeselect = useCallback(() => {
    resetSelectedClientLens(emptyObject);
  }, [resetSelectedClientLens]);

  return (
    <Card id={"clientCard" + index} className={classes.card}>
      <Grid container>
        <Grid item xs={12} lg={2} className={classes.partnerSection} container alignItems="center">
          <div className={classes.arrowTipItem}>
            <Typography noWrap className={classes.partnerName}>
              {partner.name}
            </Typography>
          </div>
        </Grid>
        <Grid item container xs={12} lg={10} direction="column" justifyContent="center" className={classes.partnerData}>
          <Grid item container xs={10} justifyContent="space-around" alignItems="center">
            <Grid item xs={2}>
              <Typography className={classes.minFont}>{t("Client.CityOrState")}</Typography>
              <Typography className={classes.reducedFont}>{`${
                (partner.city || "") + ((partner.state && " (" + partner.state + ")") || "")
              }`}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.minFont}>{t("Client.Address")}</Typography>
              <Typography className={classes.reducedFont}>{partner.address}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.minFont}>{t("Client.Zip")}</Typography>
              <Typography className={classes.reducedFont}>{partner.zip}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.minFont}>{t("Client.Phone")}</Typography>
              <Typography className={classes.reducedFont}>{partner.phone}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.minFont}>{t("Client.TaxIdNumber")}</Typography>
              <Typography className={classes.reducedFont}>{partner.taxId}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={2} justifyContent="center">
            {selected ? (
              <IconButton color="primary" onClick={handleDeselect} className={classes.checkBox} disabled={isReadOnly}>
                <CheckBox />
              </IconButton>
            ) : (
              <Button variant="outlined" className={classes.reducedFont} onClick={handleSelect}>
                {t("General.Select")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}

Client.propTypes = {
  index: PropTypes.number.isRequired,
  partner: PropTypes.object.isRequired,
  selectedClientLens: PropTypes.object.isRequired,
  resetSelectedClientLens: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
};

export default Client;
